import React, { useEffect, useState, useRef } from "react";
import { Box, Divider, TableCell, Tooltip } from "@mui/material";
import CommonCalculator from "../../../../builders/calculator/common";
import PdfGenerator from "../Pdf/RentalIncomePdf";
import {
  formatAmount,
  expensesAdjustment,
  isAutoload,
  formatHistoryAmount,
} from "../../../../utils/hooks/dataHandler";
import { JustDropDown } from "../../../utils/Form/Dropdown";
import ScheduleHeader from "../../../App/ScheduleHeader";
import ScheduleNotes from "../../ScheduleNotes";
import CommonTable from "../../../utils/CommonTable";
import { CalculatorTextField } from "../../../utils/TextField/NumericTextField";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../../utils/TextField/TextField";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RentalSettingsDialog from "./RentalSettingsDialog";
import RentalSettingsSummary from "./RentalSettingsSummary";
import SectionBox from "../../utils/SectionBox";
import ScheduleContainer from "../../utils/ScheduleContainer";
import ForeignIncomeForm from "../../utils/ForeignIncomeForm";
import CustomDialog from "../../../utils/dialog/customDialog";
import ShareOfExpenses from "../../utils/ShareOfExpenses";

const DIRECT = "Direct";
const APPORTION = "Apportion";
const NONDEDUCTIBLE = "Non-deductible";

const adjustmentSet = [DIRECT, APPORTION, NONDEDUCTIBLE];

const RentalSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [schName, setSchName] = useState("Rental Income");
  const [taxAdjustment, setTaxAdjustment] = useState("");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [unremittedSchNo, setUnremittedSchNo] = useState("");
  const [includeAddress, setIncludeAddress] = useState(true);
  const [address, setAddress] = useState({
    address_line1: null,
    address_line2: null,
    address_line3: null,
  });
  const [hasVacantPeriod, setHasVacantPeriod] = useState(false);
  const [vacantPeriods, setVacantPeriods] = useState([]);
  const [rentalPeriods, setRentalPeriods] = useState([]);
  const [grossIncomeRows, setGrossIncomeRows] = useState([]);
  const [expensesRows, setExpensesRows] = useState([]);
  const [totalGrossIncome, setTotalGrossIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netRentalIncome, setNetRentalIncome] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showAddress, setShowAddress] = useState(false);

  const [isTagToFTC, setIsTagToFTC] = useState(false);
  const [foreignTaxSuffered, setForeignTaxSuffered] = useState(0);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [showFTCSaveDialog, setShowFTCSaveDialog] = useState(false);
  const [shareOfExpenses, setShareOfExpenses] = useState(null);
  const [ftcSchNo, setFtcSchNo] = useState("");
  const ftcFormRef = useRef(null);
  const handleTagToFTC = () => {
    setIsTagToFTC(true);
  };

  const handleUnTagToFTC = () => {
    setIsTagToFTC(false);
  };

  const scrollToFTCForm = () => {
    if (ftcFormRef.current) {
      ftcFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const getTableColumns = (handleChange, setRows, options = {}) => {
    const {
      showAdjustment = false,
      adjustmentOptions = [],
      showReference = true,
      showAction = true,
      rowType,
    } = options;

    const columns = [
      {
        key: "description",
        label: "Description",
        headerAlign: "left",
        render: (row, index) => (
          <TextFieldForName
            value={row.description}
            onChange={handleChange}
            reference={{
              index: index,
              field: "description",
            }}
            disabled={isAutoload(row.status)}
            canEdit={canEditSchedule}
          />
        ),
      },
    ];

    if (showAdjustment) {
      columns.push({
        key: "adjustment",
        label: "Adjustment",
        headerAlign: "center",
        colAlign: "center",
        render: (row, index) => (
          <JustDropDown
            label=''
            value={row.adjustment}
            options={adjustmentOptions}
            onChange={(e) =>
              handleChange(e.target.value, {
                index: index,
                field: "adjustment",
              })
            }
            size='small'
            canEdit={canEditSchedule}
          />
        ),
      });
    }

    columns.push({
      key: "amount",
      label: "Amount",
      headerAlign: "right",
      colAlign: "right",
      render: (row, index) => (
        <Tooltip
          title={
            row?.historyAmount &&
            row.historyAmount.length > 0 &&
            formatHistoryAmount(row.historyAmount)
          }
          placement='top'
        >
          <Box display='inline-block' sx={{ width: "100%" }}>
            <CalculatorTextField
              value={row?.historyAmount || []}
              onChange={handleChange}
              reference={{
                index: index,
                field: "amount",
              }}
              fullWidth={true}
              disabled={isAutoload(row.status)}
              canEdit={canEditSchedule}
            />
          </Box>
        </Tooltip>
      ),
    });

    // Add Adjusted Amount column for expenses with adjustment
    if (showAdjustment) {
      columns.push({
        key: "adjustedAmount",
        label: "Adjusted Amount",
        headerAlign: "right",
        colAlign: "right",
        render: (row, index) => {
          // Calculate adjusted value
          const adjusted =
            row.amount -
            expensesAdjustment(
              row.adjustment,
              row.amount,
              vacantPeriods.length > 0,
              vacantPeriods,
              rentalPeriods
            );
          return (
            <TextFieldForViewOnly
              value={formatAmount(adjusted)}
              sx={{ textAlign: "right" }}
            />
          );
        },
      });
    }

    if (showReference) {
      columns.push({
        key: "reference",
        label: "Reference",
        headerAlign: "center",
        render: (row, index) => (
          <TextFieldForName
            value={row?.reference}
            onChange={handleChange}
            reference={{
              index: index,
              field: "reference",
            }}
            canEdit={canEditSchedule}
            viewOnlyAlign='center'
          />
        ),
      });

      columns.push({
        key: "source",
        label: "Sch",
        headerAlign: "center",
        render: (row) => (
          <TextFieldForViewOnly
            value={row.source?.sch_no || ""}
            sx={{ textAlign: "center" }}
          />
        ),
      });
    }

    if (showAction && canEditSchedule) {
      columns.push({
        key: "action",
        label: "Action",
        headerAlign: "center",
        render: (row, index) => (
          <IconButtonWithTooltip
            icon={DeleteOutlineIcon}
            tooltipTitle='Delete Row'
            onClick={() => removeRow(setRows, index, rowType)}
            color='error'
            size='small'
            disabled={isAutoload(row.status)}
          />
        ),
      });
    }

    return columns;
  };

  const getShareOfExpensesAmount = () => {
    return company.companyType === "investment"
      ? shareOfExpenses?.amount || 0
      : 0;
  };

  const calculateExpenses = (rows = []) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      setNetRentalIncome(totalGrossIncome - getShareOfExpensesAmount()); // No expenses, net income equals total gross income
      return 0; // No expenses to calculate
    }

    let total = rows.reduce(
      (sum, row) =>
        sum +
        (parseFloat(
          row.amount -
            expensesAdjustment(
              row.adjustment,
              row.amount,
              vacantPeriods.length > 0,
              vacantPeriods,
              rentalPeriods
            )
        ) || 0),
      0
    );

    setNetRentalIncome(totalGrossIncome - total - getShareOfExpensesAmount());
    return total;
  };

  const calculateDays = (startDate, endDate) => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }
    return 0;
  };

  const proceedSave = (resetFTC = false) => {
    const effectiveForeignTaxSuffered = resetFTC ? 0 : foreignTaxSuffered;
    const effectiveIsTagToFTC = resetFTC ? false : isTagToFTC;

    if (resetFTC) {
      setForeignTaxSuffered(0);
      setIsTagToFTC(false);
    }

    let finalTotalExpenses = totalExpenses;
    if (company.companyType === "investment") {
      finalTotalExpenses = finalTotalExpenses + getShareOfExpensesAmount();
    }

    schedule.name = schName;
    schedule.taxAdjustment = taxAdjustment;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      address: address,
      vacant_periods: vacantPeriods,
      rental_periods: rentalPeriods,
      grossIncomeRows: grossIncomeRows,
      expensesRows: expensesRows,
      totalAdjustment: netRentalIncome,
      totalRevenue: totalGrossIncome,
      totalExpenses: finalTotalExpenses,
      foreignTaxSuffered: effectiveForeignTaxSuffered,
      isTagToFTC: effectiveIsTagToFTC,
      doc: JSON.stringify({
        taxAdjustment,
        address: includeAddress ? address : null,
        hasVacantPeriod,
        vacantPeriod: hasVacantPeriod ? vacantPeriods : [],
        rentalPeriod: rentalPeriods,
        grossIncomeRows,
        expensesRows,
        totalGrossIncome,
        netRentalIncome,
        foreignTaxSettings: {
          foreignTaxSuffered: effectiveForeignTaxSuffered,
          isTagToFTC: effectiveIsTagToFTC,
          unremittedSchNo: unremittedSchNo,
          ftcSchNo: ftcSchNo,
        },
        notes,
        reviewNotes,
        shareOfExpenses,
      }),
    });
  };

  const handleSave = () => {
    const isUnremittedForeignIncome =
      taxAdjustment === "Unremitted Foreign Income";
    const shouldResetFTC = isUnremittedForeignIncome && netRentalIncome <= 0;
    const shouldShowFTCHint =
      isUnremittedForeignIncome &&
      netRentalIncome > 0 &&
      foreignTaxSuffered === 0;

    if (shouldResetFTC) {
      setConfirmResetOpen(true);
      return;
    }

    if (shouldShowFTCHint) {
      setShowFTCSaveDialog(true);
      return;
    }

    proceedSave(false);
  };

  const calculateTotals = (expensesData, incomeData) => {
    let c_totalGrossIncome = CommonCalculator.arraySum(incomeData);
    let c_totalExpenses = calculateExpenses(expensesData);
    const netIncome =
      c_totalGrossIncome - c_totalExpenses - getShareOfExpensesAmount();

    setTotalGrossIncome(c_totalGrossIncome);
    setTotalExpenses(c_totalExpenses);
    setNetRentalIncome(netIncome);
  };

  const handleRowChange = (value, { index, field, rowType }) => {
    const isExpense = rowType === "expense";
    const rows = isExpense ? expensesRows : grossIncomeRows;
    const setRows = isExpense ? setExpensesRows : setGrossIncomeRows;
    const otherRows = isExpense ? grossIncomeRows : expensesRows;

    const updatedRows = [...rows];
    if (field === "amount") {
      updatedRows[index]["historyAmount"] = value;
      updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);
      calculateTotals(
        isExpense ? updatedRows : otherRows,
        isExpense ? otherRows : updatedRows
      );
    } else {
      updatedRows[index][field] = value;
    }

    if (isExpense && ["adjustment"].includes(field)) {
      calculateTotals(
        isExpense ? updatedRows : otherRows,
        isExpense ? otherRows : updatedRows
      );
    }

    setRows(updatedRows);
  };

  const handleGrossIncomeChange = (value, params) => {
    handleRowChange(value, { ...params, rowType: "income" });
  };

  const handleExpensesChange = (value, params) => {
    handleRowChange(value, { ...params, rowType: "expense" });
  };

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setTaxAdjustment(schedule.taxAdjustment);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      if (
        scheduleDetails.address_line1 ||
        scheduleDetails.address_line2 ||
        scheduleDetails.address_line3
      ) {
        setIncludeAddress(true);
        setAddress({
          address_line1: scheduleDetails.address_line1,
          address_line2: scheduleDetails.address_line2,
          address_line3: scheduleDetails.address_line3,
        });
      } else {
        setIncludeAddress(false);
        setAddress({
          address_line1: null,
          address_line2: null,
          address_line3: null,
        });
      }

      // Handle legacy single period format
      if (scheduleDetails.vacant_start || scheduleDetails.vacant_end) {
        setHasVacantPeriod(true);
        setVacantPeriods([
          {
            startDate: scheduleDetails.vacant_start,
            endDate: scheduleDetails.vacant_end,
            days: calculateDays(
              scheduleDetails.vacant_start,
              scheduleDetails.vacant_end
            ),
          },
        ]);
      } else if (
        scheduleDetails.vacant_periods &&
        scheduleDetails.vacant_periods.length > 0
      ) {
        setHasVacantPeriod(true);
        setVacantPeriods(scheduleDetails.vacant_periods);
      } else {
        setVacantPeriods([]);
      }

      // Handle legacy single period format
      if (scheduleDetails.rental_start || scheduleDetails.rental_end) {
        setHasVacantPeriod(true);
        setRentalPeriods([
          {
            startDate: scheduleDetails.rental_start,
            endDate: scheduleDetails.rental_end,
            days: calculateDays(
              scheduleDetails.rental_start,
              scheduleDetails.rental_end
            ),
          },
        ]);
      } else if (
        scheduleDetails.rental_periods &&
        scheduleDetails.rental_periods.length > 0
      ) {
        setRentalPeriods(scheduleDetails.rental_periods);
      } else {
        setRentalPeriods([]);
      }

      setGrossIncomeRows(
        scheduleDetails.grossIncomeRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );
      setExpensesRows(
        scheduleDetails.expensesRows.map((row) => {
          if (row.amount < 0) {
            row.amount = -row.amount;
          }

          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );

      setForeignTaxSuffered(scheduleDetails.foreignTaxSuffered || 0);
      setIsTagToFTC(scheduleDetails.isTagToFTC || false);
      setUnremittedSchNo(scheduleDetails.schRef || "");
      setFtcSchNo(scheduleDetails.foreignTaxCreditScheduleRef || "");
      setShareOfExpenses(scheduleDetails.shareOfExpenses || null);

      //calculation
      calculateTotals(
        scheduleDetails.expensesRows,
        scheduleDetails.grossIncomeRows
      );
    }
  }, []);

  useEffect(() => {
    setTotalExpenses(calculateExpenses(expensesRows));
  }, [vacantPeriods]);

  const addRow = (setRows) => {
    setRows((prevRows) => [
      ...prevRows,
      {
        description: "",
        amount: "",
        reference: "",
      },
    ]);
  };

  const removeRow = (setRows, index, rowType) => {
    if (rowType === "expense") {
      const newExpensesRows = expensesRows.filter((_, i) => i !== index);
      setExpensesRows(newExpensesRows);
      calculateTotals(newExpensesRows, grossIncomeRows);
    } else if (rowType === "income") {
      const newGrossIncomeRows = grossIncomeRows.filter((_, i) => i !== index);
      setGrossIncomeRows(newGrossIncomeRows);
      calculateTotals(expensesRows, newGrossIncomeRows);
    } else {
      setRows((prevRows) => prevRows.filter((_, i) => i !== index));
    }
  };

  // Helper for summary (now only rental and vacant period)
  const addressLines = [
    address.address_line1,
    address.address_line2,
    address.address_line3,
  ].filter(Boolean);

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          companyType: company.companyType,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          currency: company.currency,
          companyType: company.companyType,
          handleSave,
          deleteSchedule,
          showSettings: true,
          onSettingsClick: () => setSettingsOpen(true),
          hasActiveSettings: includeAddress || hasVacantPeriod,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          address={includeAddress ? address : null}
          hasVacantPeriod={hasVacantPeriod}
          vacantPeriods={vacantPeriods}
          rentalPeriods={rentalPeriods}
          grossIncomeRows={grossIncomeRows}
          expensesRows={expensesRows}
          totalGrossIncome={totalGrossIncome}
          netRentalIncome={netRentalIncome}
          foreignTaxSettings={{
            foreignTaxSuffered,
            isTagToFTC,
            unremittedSchNo,
            ftcSchNo,
          }}
          notes={notes}
          reviewNotes={reviewNotes}
          shareOfExpenses={shareOfExpenses}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={taxAdjustment}
        allowedToEditSchName={canEditSchedule}
        allowedToAdjustTax={canEditSchedule}
        taxAdjustOptions={["", "Added Back", "Unremitted Foreign Income"]}
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        updateAdjustment={(newAdjustment) => {
          schedule.taxAdjustment = newAdjustment;
          if (newAdjustment !== "Unremitted Foreign Income") {
            setIsTagToFTC(false);
            setForeignTaxSuffered(0);
          }
          setTaxAdjustment(newAdjustment);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            <RentalSettingsSummary
              includeAddress={includeAddress}
              addressLines={addressLines}
              rentalPeriods={rentalPeriods}
              hasVacantPeriod={hasVacantPeriod}
              vacantPeriods={vacantPeriods}
              showAddress={showAddress}
              setShowAddress={setShowAddress}
              onEditSettings={() => setSettingsOpen(true)}
              canEdit={canEditSchedule}
            />
            {/* Gross Rental Income */}
            <SectionBox
              title='Gross Rental Income'
              onAdd={() => addRow(setGrossIncomeRows)}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={grossIncomeRows}
                columns={getTableColumns(
                  handleGrossIncomeChange,
                  setGrossIncomeRows,
                  { rowType: "income" }
                )}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Total Gross Income
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: "bold" }}>
                  {formatAmount(totalGrossIncome)}
                </TableCell>
                <TableCell colSpan={3}></TableCell>
              </CommonTable>
            </SectionBox>
            <Divider sx={{ marginY: 4 }} />
            {/* Rental Expenses */}
            <SectionBox
              title='Rental Expenses'
              onAdd={() => addRow(setExpensesRows)}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={expensesRows}
                columns={getTableColumns(
                  handleExpensesChange,
                  setExpensesRows,
                  {
                    showAdjustment: true,
                    adjustmentOptions: adjustmentSet,
                    rowType: "expense",
                  }
                )}
              >
                <TableCell />
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Total Expenses
                </TableCell>
                <TableCell align='right'>
                  {formatAmount(
                    expensesRows.reduce((sum, row) => {
                      return sum + row.amount;
                    }, 0)
                  )}
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: "bold" }}>
                  {formatAmount(totalExpenses)}
                </TableCell>
                <TableCell />
                <TableCell colSpan={2}></TableCell>
              </CommonTable>
            </SectionBox>

            {shareOfExpenses && (
              <ShareOfExpenses
                companyType={company.companyType}
                shareOfExpenses={shareOfExpenses}
              />
            )}
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />

      <Box ref={ftcFormRef}>
        {/* Net Rental Income */}
        <ForeignIncomeForm
          canEdit={canEditSchedule}
          steps={[
            {
              section: "Rental Income",
              amount: formatAmount(totalGrossIncome),
              tooltip:
                "Total income from rental properties. This includes all rental-related income before any deductions.",
            },
            {
              section: "Rental Expenses",
              amount: `(${formatAmount(
                expensesRows.reduce((sum, row) => sum + row.amount, 0)
              )})`,
              tooltip:
                "Total rental expenses before any disallowance or apportionment.",
            },
            {
              section: "Disallowed Rental Expenses",
              amount: formatAmount(
                Math.abs(
                  expensesRows.reduce((sum, row) => sum + row.amount, 0) -
                    totalExpenses
                )
              ),
              tooltip:
                "Expenses that are not allowed for deduction, including apportioned and non-deductible amounts.",
            },
            ...(company.companyType === "investment" && shareOfExpenses
              ? [
                  {
                    section: "Share of Expenses",
                    amount: formatAmount(-shareOfExpenses?.amount || 0),
                    tooltip: `Share of expenses from ${
                      shareOfExpenses?.source ?? ""
                    }.`,
                  },
                ]
              : []),
          ]}
          total={{
            title: "Net Rental Income",
            amount: formatAmount(netRentalIncome),
            tooltip:
              "Formula: Rental Income - Allowed Rental Expenses. This represents the net amount of rental income after deducting all allowed expenses.",
          }}
          isTagToFTC={isTagToFTC}
          taxAdjustment={taxAdjustment}
          grossIncome={totalGrossIncome}
          allowableExpenses={totalExpenses}
          netOtherIncome={netRentalIncome}
          foreignTaxSuffered={foreignTaxSuffered}
          onForeignTaxSufferedChange={(taxAmount) => {
            setForeignTaxSuffered(taxAmount);
          }}
          onTagToFTC={handleTagToFTC}
          onUnTagToFTC={handleUnTagToFTC}
        />
      </Box>

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />

      {/* Settings Dialog */}
      <RentalSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        includeAddress={includeAddress}
        setIncludeAddress={setIncludeAddress}
        address={address}
        setAddress={setAddress}
        hasVacantPeriod={hasVacantPeriod}
        setHasVacantPeriod={setHasVacantPeriod}
        rentalPeriods={rentalPeriods}
        setRentalPeriods={setRentalPeriods}
        vacantPeriods={vacantPeriods}
        setVacantPeriods={setVacantPeriods}
        calculateDays={calculateDays}
        financialYearStart={company.financialYearStart}
        financialYearEnd={company.financialYearEnd}
      />

      <CustomDialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        onConfirm={() => {
          setConfirmResetOpen(false);
          proceedSave(true);
        }}
        title={"Reset Foreign Tax Credit?"}
        description={
          "Net income is zero or negative. Saving will reset Foreign Tax Suffered and remove FTC tagging for this schedule. Do you want to continue?"
        }
        type='warning'
      />

      {/* FTC Save Dialog */}
      <CustomDialog
        open={showFTCSaveDialog}
        onClose={() => {
          setShowFTCSaveDialog(false);
          // Scroll after dialog is closed to prevent interference
          setTimeout(() => {
            scrollToFTCForm();
          }, 100);
        }}
        onConfirm={() => {
          setShowFTCSaveDialog(false);
          proceedSave(false);
        }}
        title={"Has the foreign income suffered foreign tax?"}
        description={
          "Please indicate the amount of foreign tax suffered (if any)​ in the 'FTC Processing' section before saving. If no foreign tax was suffered, click 'Confirm' to save. Otherwise, click 'Cancel' to input the amount of foreign tax suffered."
        }
        type='info'
      />
    </Box>
  );
};

export default RentalSchedule;
